import { z } from 'zod';

/**
 * Motor de escala (M07). Ele **sugere**, nunca impõe: cada vaga vem com uma
 * pessoa, a justificativa em português e as alternativas em ordem. Nada vira
 * escala sem alguém da liderança aceitar.
 */

export const PESOS_DO_MOTOR = [
  'intervalo',
  'equilibrio',
  'preferencia',
  'diversidade',
  'aprendiz',
  'continuidade',
  'disponibilidade',
] as const;
export type PesoDoMotor = (typeof PESOS_DO_MOTOR)[number];

export const PESO_MINIMO = 0;
export const PESO_MAXIMO = 5;

const peso = z.coerce.number().int().min(PESO_MINIMO).max(PESO_MAXIMO);

export const schedulingWeightsSchema = z.object({
  /** Tempo desde a última escala: quem está há mais tempo parado sobe. */
  intervalo: peso,
  /** Equilíbrio de carga no período: quem serviu menos entra primeiro. */
  equilibrio: peso,
  /** Preferência declarada pela função. */
  preferencia: peso,
  /** Não repetir sempre a mesma dupla ou trio. */
  diversidade: peso,
  /** Formar gente nova: aprendiz acompanhada de uma referência. */
  aprendiz: peso,
  /** Manter quem vinha fazendo aquela função. */
  continuidade: peso,
  /** Dia e turno que a pessoa marcou como habituais. */
  disponibilidade: peso,
});
export type SchedulingWeights = z.infer<typeof schedulingWeightsSchema>;

/**
 * O padrão do produto. Prioriza o que a igreja mais reclama quando falta:
 * gente servindo semanas seguidas, e carga desigual entre quem serve.
 */
export const PESOS_PADRAO: SchedulingWeights = {
  intervalo: 3,
  equilibrio: 3,
  preferencia: 2,
  diversidade: 2,
  aprendiz: 1,
  continuidade: 1,
  disponibilidade: 2,
};

export const ROTULO_DO_PESO: Record<PesoDoMotor, string> = {
  intervalo: 'Descanso entre escalas',
  equilibrio: 'Equilíbrio de carga',
  preferencia: 'Preferência da pessoa',
  diversidade: 'Variar a composição',
  aprendiz: 'Formar aprendizes',
  continuidade: 'Continuidade na função',
  disponibilidade: 'Dia e turno habituais',
};

export const AJUDA_DO_PESO: Record<PesoDoMotor, string> = {
  intervalo: 'Quanto pesa estar há mais tempo sem servir.',
  equilibrio: 'Quanto pesa ter servido menos vezes no período.',
  preferencia: 'Quanto pesa gostar (ou evitar) esta função.',
  diversidade: 'Quanto pesa não repetir sempre a mesma equipe.',
  aprendiz: 'Quanto pesa dar espaço a quem está aprendendo, com alguém junto.',
  continuidade: 'Quanto pesa manter quem já vinha fazendo esta função.',
  disponibilidade: 'Quanto pesa o dia e o turno que a pessoa marcou como habituais.',
};

/** O que aparece na régua de cada peso. */
export const NIVEL_DO_PESO: Record<number, string> = {
  0: 'Ignorar',
  1: 'Pouco',
  2: 'Normal',
  3: 'Bastante',
  4: 'Muito',
  5: 'Decisivo',
};

export const updateSchedulingWeightsSchema = z.object({
  weights: schedulingWeightsSchema.partial().nullable(),
});
export type UpdateSchedulingWeightsInput = z.infer<typeof updateSchedulingWeightsSchema>;

// ── Sugestão ─────────────────────────────────────────────────────────────────

export const suggestScheduleSchema = z
  .object({
    /** Um culto específico, ou o período inteiro da grade. */
    eventId: z.string().uuid().nullish(),
    from: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullish(),
    to: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullish(),
    ministryId: z.string().uuid().nullish(),
    congregationId: z.string().uuid().nullish(),
    /** Vagas que já têm gente ficam de fora por padrão. */
    apenasVagasAbertas: z.boolean().default(true),
  })
  .refine((dados) => Boolean(dados.eventId) || Boolean(dados.from && dados.to), {
    message: 'Escolha um evento ou um período',
    path: ['eventId'],
  });
export type SuggestScheduleInput = z.infer<typeof suggestScheduleSchema>;

export const suggestionFactorSchema = z.object({
  rotulo: z.string(),
  pontos: z.number(),
});

export const suggestionPersonSchema = z.object({
  personId: z.string().uuid(),
  nome: z.string(),
  fotoUrl: z.string().nullable(),
  nivel: z.enum(['APPRENTICE', 'QUALIFIED', 'REFERENCE']).nullable(),
  pontuacao: z.number(),
  /** A frase que responde "por que essa pessoa?". */
  justificativa: z.string(),
  fatores: z.array(suggestionFactorSchema),
  avisos: z.array(
    z.object({
      tipo: z.string(),
      severidade: z.enum(['impeditivo', 'atencao']),
      mensagem: z.string(),
    }),
  ),
});
export type SuggestionPerson = z.infer<typeof suggestionPersonSchema>;

export const scheduleSuggestionSchema = z.object({
  eventPositionId: z.string().uuid(),
  eventId: z.string().uuid(),
  eventTitle: z.string(),
  eventStartsAt: z.string().datetime(),
  functionName: z.string(),
  ministryId: z.string().uuid(),
  ministryName: z.string(),
  ordem: z.number().int(),
  escolhido: suggestionPersonSchema.nullable(),
  alternativas: z.array(suggestionPersonSchema),
  /** Por que a vaga ficou sem ninguém. Nunca vazio quando não há escolhido. */
  semGente: z.string().nullable(),
});
export type ScheduleSuggestion = z.infer<typeof scheduleSuggestionSchema>;

export const scheduleSuggestionsSchema = z.object({
  items: z.array(scheduleSuggestionSchema),
  vagasSemGente: z.number().int(),
  /** Os pesos que valeram, por departamento — é o que a tela explica. */
  pesos: z.record(z.string(), schedulingWeightsSchema),
});
export type ScheduleSuggestions = z.infer<typeof scheduleSuggestionsSchema>;

/** O líder aceita tudo, aceita parte, ou troca a pessoa antes de aceitar. */
export const applySuggestionsSchema = z.object({
  items: z
    .array(
      z.object({
        eventPositionId: z.string().uuid(),
        personId: z.string().uuid(),
        /** A sugestão vinha com aviso e a liderança aceitou assim mesmo. */
        aceitarAvisos: z.boolean().default(false),
      }),
    )
    .min(1, 'Escolha pelo menos uma sugestão')
    .max(500),
});
export type ApplySuggestionsInput = z.infer<typeof applySuggestionsSchema>;

export const applySuggestionsResultSchema = z.object({
  criadas: z.number().int(),
  recusadas: z.array(z.object({ eventPositionId: z.string(), motivo: z.string() })),
});
export type ApplySuggestionsResult = z.infer<typeof applySuggestionsResultSchema>;
